import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { products } from '../data/mockData';

// Editorial label per bundle id — confident, not flashy
const BUNDLE_META = {
  28: { tag: 'For Body Composition', accent: '#E8B477', accentBg: 'rgba(232, 180, 119, 0.10)', accentBorder: 'rgba(232, 180, 119, 0.4)' },
  29: { tag: 'For Skin & Recovery', accent: '#D4A373', accentBg: 'rgba(212, 163, 115, 0.10)', accentBorder: 'rgba(212, 163, 115, 0.4)' },
  30: { tag: 'For Total Transformation', accent: '#FCD34D', accentBg: 'rgba(252, 211, 77, 0.10)', accentBorder: 'rgba(252, 211, 77, 0.4)' },
  31: { tag: 'For Longevity', accent: '#C78C50', accentBg: 'rgba(199, 140, 80, 0.10)', accentBorder: 'rgba(199, 140, 80, 0.4)' },
};

const BundlesSection = () => {
  const bundles = products.filter((p) => p.isBundle);
  if (bundles.length === 0) return null;

  return (
    <section
      data-testid="bundles-section"
      className="relative py-16 sm:py-24 overflow-hidden"
      style={{ backgroundColor: '#0C0A09' }}
    >
      {/* Layered ambient glows */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse at top left, rgba(252, 211, 77, 0.18) 0%, transparent 50%), radial-gradient(ellipse at bottom right, rgba(212, 163, 115, 0.22) 0%, transparent 55%), radial-gradient(ellipse at center, rgba(220, 38, 38, 0.06) 0%, transparent 70%)',
        }}
      />
      {/* Grain */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.07]"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160' viewBox='0 0 160 160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' /></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.6'/></svg>\")",
        }}
      />

      {/* Animated shimmer line */}
      <div
        className="absolute top-0 left-0 right-0 h-px"
        style={{
          background:
            'linear-gradient(90deg, transparent 0%, #D4A373 50%, transparent 100%)',
          opacity: 0.6,
        }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col items-center text-center mb-12 sm:mb-16">
          {/* Subtle kicker */}
          <div
            className="inline-flex items-center gap-3 mb-6"
          >
            <div className="h-px w-10" style={{ backgroundColor: '#D4A373' }} />
            <span
              className="text-[11px] font-semibold uppercase tracking-[0.4em]"
              style={{ color: '#D4A373' }}
            >
              Curated Stacks
            </span>
            <div className="h-px w-10" style={{ backgroundColor: '#D4A373' }} />
          </div>

          {/* Clean editorial headline */}
          <h2
            className="font-bold mb-5 leading-[0.95]"
            style={{
              fontFamily: 'Playfair Display, serif',
              fontSize: 'clamp(2.75rem, 7vw, 5.5rem)',
              color: '#F7F3EE',
            }}
          >
            The{' '}
            <span
              style={{
                background: 'linear-gradient(135deg, #FCD34D 0%, #D4A373 50%, #C78C50 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                fontStyle: 'italic',
              }}
            >
              Bundles
            </span>
          </h2>

          <p
            className="max-w-2xl text-base sm:text-lg leading-relaxed mb-6"
            style={{ color: '#B5A79E' }}
          >
            Purpose-built peptide stacks priced below retail. Every bundle ships with{' '}
            <span style={{ color: '#F7F3EE', fontWeight: 600 }}>15 premium syringes</span>,{' '}
            <span style={{ color: '#F7F3EE', fontWeight: 600 }}>3ml BAC water</span>, and{' '}
            <span style={{ color: '#F7F3EE', fontWeight: 600 }}>alcohol prep pads</span>.
          </p>

          {/* Stat strip */}
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-center">
            <div className="flex flex-col items-center">
              <span className="text-2xl font-bold" style={{ color: '#D4A373', fontFamily: 'Playfair Display, serif' }}>
                4
              </span>
              <span className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: '#B5A79E' }}>
                Stacks
              </span>
            </div>
            <div className="h-8 w-px" style={{ backgroundColor: '#362B25' }} />
            <div className="flex flex-col items-center">
              <span className="text-2xl font-bold" style={{ color: '#D4A373', fontFamily: 'Playfair Display, serif' }}>
                $20
              </span>
              <span className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: '#B5A79E' }}>
                Max Savings
              </span>
            </div>
            <div className="h-8 w-px" style={{ backgroundColor: '#362B25' }} />
            <div className="flex flex-col items-center">
              <span className="text-2xl font-bold" style={{ color: '#D4A373', fontFamily: 'Playfair Display, serif' }}>
                100%
              </span>
              <span className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: '#B5A79E' }}>
                Complete Kit
              </span>
            </div>
          </div>
        </div>

        {/* Bundle Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6">
          {bundles.map((bundle, idx) => {
            const meta = BUNDLE_META[bundle.id] || BUNDLE_META[28];
            const savings = (bundle.originalPrice || bundle.price) - bundle.price;
            const itemCount = bundle.bundleContents
              ? bundle.bundleContents.filter((c) => !['Premium Syringes', 'Bacteriostatic Water', 'Alcohol prep pads'].includes(c.name)).length
              : 0;
            return (
              <Link
                key={bundle.id}
                to={`/product/${bundle.id}`}
                data-testid={`bundle-card-${bundle.id}`}
                className="group relative flex flex-col rounded-2xl overflow-hidden transition-all duration-500 hover:scale-[1.015]"
                style={{
                  background: 'linear-gradient(135deg, #1A1513 0%, #221A14 50%, #1A1513 100%)',
                  border: '1px solid rgba(212, 163, 115, 0.3)',
                  boxShadow: '0 0 0 rgba(212, 163, 115, 0)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = `0 0 48px ${meta.accentBorder}, 0 0 24px rgba(212, 163, 115, 0.25)`;
                  e.currentTarget.style.borderColor = meta.accent;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = '0 0 0 rgba(212, 163, 115, 0)';
                  e.currentTarget.style.borderColor = 'rgba(212, 163, 115, 0.3)';
                }}
              >
                {/* Glow corner accent */}
                <div
                  className="absolute -top-20 -right-20 w-64 h-64 rounded-full pointer-events-none opacity-40 transition-opacity duration-500 group-hover:opacity-70"
                  style={{ background: `radial-gradient(circle, ${meta.accent} 0%, transparent 65%)` }}
                />

                {/* Top tag pill */}
                <div
                  className="absolute top-4 right-4 z-10 inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-widest"
                  style={{
                    backgroundColor: meta.accentBg,
                    color: meta.accent,
                    border: `1px solid ${meta.accentBorder}`,
                    backdropFilter: 'blur(8px)',
                  }}
                >
                  {meta.tag}
                </div>

                <div className="flex flex-col sm:flex-row items-stretch flex-1 relative">
                  {/* Image side */}
                  <div
                    className="relative flex items-center justify-center p-6 sm:p-8 sm:w-2/5"
                    style={{
                      background: 'radial-gradient(ellipse at center, rgba(212, 163, 115, 0.08) 0%, transparent 70%)',
                      minHeight: '220px',
                    }}
                  >
                    {/* Savings tag */}
                    {savings > 0 && (
                      <div
                        className="absolute top-4 left-4 z-10 px-3 py-1.5 rounded"
                        style={{
                          backgroundColor: '#0C0A09',
                          border: `1px solid ${meta.accent}`,
                        }}
                      >
                        <p className="text-[9px] uppercase tracking-widest font-bold leading-none mb-0.5" style={{ color: '#B5A79E' }}>
                          Save
                        </p>
                        <p
                          className="font-bold leading-none"
                          style={{ color: meta.accent, fontSize: '18px', fontFamily: 'Playfair Display, serif' }}
                        >
                          ${savings}
                        </p>
                      </div>
                    )}
                    <img
                      src={bundle.imageUrl}
                      alt={bundle.name}
                      className="max-h-44 sm:max-h-56 max-w-full object-contain transition-transform duration-700 group-hover:scale-110"
                      style={{
                        filter: 'drop-shadow(0 12px 24px rgba(0,0,0,0.5))',
                      }}
                    />
                  </div>

                  {/* Content side */}
                  <div className="flex flex-col flex-1 p-5 sm:p-6 sm:w-3/5">
                    <span
                      className="text-[10px] uppercase tracking-[0.2em] font-bold mb-1"
                      style={{ color: meta.accent }}
                    >
                      Bundle · {itemCount} Peptide{itemCount === 1 ? '' : 's'} + Supplies
                    </span>
                    <h3
                      className="text-2xl sm:text-3xl font-bold mb-3 leading-tight"
                      style={{ color: '#F7F3EE', fontFamily: 'Playfair Display, serif' }}
                    >
                      {bundle.name}
                    </h3>

                    {/* What's inside chips */}
                    {bundle.bundleContents && (
                      <div className="flex flex-wrap gap-1.5 mb-4">
                        {bundle.bundleContents
                          .filter((c) => !['Premium Syringes', 'Bacteriostatic Water', 'Alcohol prep pads'].includes(c.name))
                          .map((item) => (
                            <span
                              key={item.name}
                              className="text-[11px] px-2.5 py-1 rounded-full font-semibold"
                              style={{
                                backgroundColor: 'rgba(212, 163, 115, 0.1)',
                                color: '#D4A373',
                                border: '1px solid rgba(212, 163, 115, 0.3)',
                              }}
                            >
                              {item.name}
                            </span>
                          ))}
                      </div>
                    )}

                    {/* Includes line */}
                    <p className="text-xs leading-relaxed mb-5" style={{ color: '#B5A79E' }}>
                      <span style={{ color: '#F7F3EE', fontWeight: 600 }}>Includes:</span> 15 Premium Syringes · 3ml BAC Water · Alcohol Prep Pads
                    </p>

                    {/* Price block */}
                    <div className="mt-auto flex items-end justify-between gap-4 pt-4" style={{ borderTop: '1px solid rgba(212, 163, 115, 0.15)' }}>
                      <div>
                        <div className="flex items-baseline gap-2 mb-0.5">
                          <span
                            className="text-4xl sm:text-5xl font-bold leading-none"
                            style={{
                              color: '#D4A373',
                              fontFamily: 'Playfair Display, serif',
                            }}
                          >
                            ${bundle.price}
                          </span>
                          {bundle.originalPrice && (
                            <span
                              className="text-base line-through font-semibold"
                              style={{ color: '#5A4A3F' }}
                            >
                              ${bundle.originalPrice}
                            </span>
                          )}
                        </div>
                        <p className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: '#B5A79E' }}>
                          One-time pack · Limited stock
                        </p>
                      </div>

                      <div
                        className="flex-shrink-0 inline-flex items-center gap-2 px-5 py-3 rounded-lg font-semibold uppercase text-xs tracking-wider transition-all group-hover:gap-3"
                        style={{
                          backgroundColor: '#F7F3EE',
                          color: '#0C0A09',
                          boxShadow: '0 4px 16px rgba(212, 163, 115, 0.2)',
                        }}
                      >
                        View Stack
                        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        {/* Footer line */}
        <div className="text-center mt-12 flex items-center justify-center gap-3">
          <div className="h-px w-12" style={{ backgroundColor: '#362B25' }} />
          <span className="text-[10px] font-semibold uppercase tracking-[0.35em]" style={{ color: '#B5A79E' }}>
            Stack smarter · For research use only
          </span>
          <div className="h-px w-12" style={{ backgroundColor: '#362B25' }} />
        </div>
      </div>
    </section>
  );
};

export default BundlesSection;
